import { useEffect, useState } from 'react'
import { Building2, Check, User } from 'lucide-react'
import { useDatos } from '../store/datos'
import { usuarioActual } from '../store/sesion'
import { useUI } from '../store/ui'
import { Hoja } from './ui/Hoja'
import { AreaTexto, Boton, Campo, Entrada, Lista, Segmentado } from './ui/base'
import { CampoDireccion } from './CampoDireccion'
import { IconoWhatsApp } from './IconoWhatsApp'
import { formatRut, rutValido } from '../lib/format'
import { PAISES_TELEFONO, separarTelefono, telefonoLuceValido, unirTelefono } from '../lib/telefono'
import { ESTILO_WHATSAPP } from '../lib/tonos'
import { uid } from '../lib/cn'
import { RUBROS, type Cliente, type TipoCliente } from '../types'

const vacio = (tipo: TipoCliente): Cliente => ({
  id: uid('c'),
  tipo,
  nombre: '',
  creado: new Date().toISOString(),
})

export function FormularioCliente({
  abierto,
  onCerrar,
  cliente,
  tipoInicial = 'empresa',
  onGuardado,
}: {
  abierto: boolean
  onCerrar: () => void
  cliente?: Cliente
  tipoInicial?: TipoCliente
  onGuardado?: (id: string) => void
}) {
  const guardarCliente = useDatos((s) => s.guardarCliente)
  const clientes = useDatos((s) => s.clientes)
  const avisar = useUI((s) => s.avisar)
  const [forma, setForma] = useState<Cliente>(cliente ?? vacio(tipoInicial))
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [guardando, setGuardando] = useState(false)
  // Si el cliente ya traía coordenadas (de una edición anterior o de
  // cuando Despachos la geocodificó sola), la dirección arranca como
  // confirmada. Apenas se toca el texto deja de estarlo —hay que
  // volver a elegirla de la lista para que cuente como confirmada de
  // nuevo.
  const [direccionConfirmada, setDireccionConfirmada] = useState(
    () => !!(cliente?.lat && cliente?.lng),
  )

  useEffect(() => {
    if (abierto) {
      setForma(cliente ? { ...cliente } : vacio(tipoInicial))
      setErrores({})
      setDireccionConfirmada(!!(cliente?.lat && cliente?.lng))
    }
  }, [abierto, cliente, tipoInicial])

  const set = <K extends keyof Cliente>(campo: K, valor: Cliente[K]) =>
    setForma((f) => ({ ...f, [campo]: valor }))

  const esEmpresa = forma.tipo === 'empresa'
  const { dial, numero } = separarTelefono(forma.telefono)
  const telefonoValido = telefonoLuceValido(dial, numero)
  const telefonoDigitos = `${dial}${numero.trim().replace(/[^\d]/g, '')}`.replace('+', '')

  // Obligatorio de verdad, solo lo mínimo para poder despachar: nombre,
  // dirección y teléfono. Todo lo demás (RUT, correo, comuna, contacto,
  // apellido) queda abierto —se completa después, y mientras tanto
  // Inicio le recuerda al vendedor que a ese cliente le falta ficha.
  const validar = () => {
    const e: Record<string, string> = {}
    if (!forma.nombre.trim())
      e.nombre = esEmpresa ? 'Escribe la razón social.' : 'Escribe el nombre.'
    if (forma.rut?.trim() && !rutValido(forma.rut)) e.rut = 'Ese RUT no es válido.'
    if (
      forma.rut?.trim() &&
      clientes.some((c) => c.id !== forma.id && c.rut && c.rut === formatRut(forma.rut!))
    )
      e.rut = 'Ya hay un cliente con ese RUT.'
    if (!numero.trim()) e.telefono = 'Escribe el teléfono.'
    if (forma.email?.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(forma.email))
      e.email = 'Revisa el correo.'
    if (!forma.direccion?.trim()) e.direccion = 'Escribe la dirección.'
    setErrores(e)
    return Object.keys(e).length === 0
  }

  const enviar = async (ev?: React.FormEvent) => {
    ev?.preventDefault()
    if (!validar()) return
    setGuardando(true)
    try {
      const limpio: Cliente = {
        ...forma,
        nombre: forma.nombre.trim(),
        apellido: forma.apellido?.trim() || undefined,
        rut: forma.rut?.trim() ? formatRut(forma.rut) : undefined,
        // Por si acaso: nunca guardar solo el prefijo de país sin
        // ningún dígito (validar() ya lo bloquea antes de llegar acá).
        telefono: numero.trim() ? forma.telefono : undefined,
      }
      await guardarCliente(limpio, usuarioActual())
      avisar(cliente ? 'Cliente actualizado' : 'Cliente creado')
      onGuardado?.(limpio.id)
      onCerrar()
    } catch (err) {
      avisar(err instanceof Error ? err.message : 'No se pudo guardar.', 'error')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <Hoja
      abierta={abierto}
      onCerrar={onCerrar}
      titulo={cliente ? 'Editar cliente' : 'Nuevo cliente'}
      descripcion="Se guarda una vez y después basta escribir las primeras letras"
      pie={
        <>
          <Boton variante="secundario" bloque onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton bloque cargando={guardando} onClick={() => enviar()}>
            {cliente ? 'Guardar cambios' : 'Crear cliente'}
          </Boton>
        </>
      }
    >
      <form onSubmit={enviar} className="space-y-4 pb-2">
        {!cliente && (
          <Segmentado
            valor={forma.tipo}
            onChange={(t) => setForma({ ...forma, tipo: t })}
            opciones={[
              { valor: 'empresa', etiqueta: 'Empresa', icono: <Building2 className="size-4" /> },
              { valor: 'particular', etiqueta: 'Particular', icono: <User className="size-4" /> },
            ]}
          />
        )}

        {esEmpresa ? (
          <>
            <Campo etiqueta="Nombre de la empresa" requerido error={errores.nombre}>
              <Entrada
                value={forma.nombre}
                onChange={(e) => set('nombre', e.target.value)}
                placeholder="Pastelería Dulce Vida"
                autoFocus
              />
            </Campo>
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo etiqueta="Rubro">
                <Lista
                  value={forma.rubro ?? 'otro'}
                  onChange={(e) => set('rubro', e.target.value as Cliente['rubro'])}
                >
                  {RUBROS.map((r) => (
                    <option key={r.valor} value={r.valor}>
                      {r.etiqueta}
                    </option>
                  ))}
                </Lista>
              </Campo>
              <Campo etiqueta="RUT" error={errores.rut}>
                <Entrada
                  value={forma.rut ?? ''}
                  onChange={(e) => set('rut', e.target.value)}
                  onBlur={(e) => e.target.value && set('rut', formatRut(e.target.value))}
                  placeholder="76.412.880-1"
                  className="codigo"
                />
              </Campo>
            </div>
            <Campo etiqueta="Contacto principal" error={errores.contacto}>
              <Entrada
                value={forma.contacto ?? ''}
                onChange={(e) => set('contacto', e.target.value)}
                placeholder="Andrea Pérez"
              />
            </Campo>
          </>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo etiqueta="Nombre" requerido error={errores.nombre}>
              <Entrada
                value={forma.nombre}
                onChange={(e) => set('nombre', e.target.value)}
                placeholder="María"
                autoFocus
              />
            </Campo>
            <Campo etiqueta="Apellido" error={errores.apellido}>
              <Entrada
                value={forma.apellido ?? ''}
                onChange={(e) => set('apellido', e.target.value)}
                placeholder="González"
              />
            </Campo>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Teléfono" requerido error={errores.telefono}>
            <div className="flex gap-2">
              <Lista
                value={dial}
                onChange={(e) => set('telefono', unirTelefono(e.target.value, numero))}
                aria-label="País"
                className="!w-[122px] shrink-0"
              >
                {PAISES_TELEFONO.map((p) => (
                  <option key={p.dial} value={p.dial}>
                    {p.bandera} {p.dial}
                  </option>
                ))}
              </Lista>
              <div className="relative min-w-0 flex-1">
                <Entrada
                  type="tel"
                  inputMode="tel"
                  value={numero}
                  onChange={(e) => set('telefono', unirTelefono(dial, e.target.value))}
                  placeholder="9 1234 5678"
                  className={numero.trim() ? 'pr-10' : ''}
                />
                {numero.trim() && (
                  <span className="pointer-events-none absolute top-1/2 right-3.5 -translate-y-1/2">
                    {telefonoValido ? (
                      <Check className="size-4 text-lima-500" strokeWidth={3} />
                    ) : null}
                  </span>
                )}
              </div>
            </div>

            {/* La verificación automática de WhatsApp Business necesita
                una cuenta de Meta verificada —mientras tanto, esto deja
                comprobar con un vistazo que el número existe de
                verdad: si abre un chat con nombre o foto, es real. */}
            {telefonoValido && (
              <a
                href={`https://wa.me/${telefonoDigitos}`}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-bold transition-transform active:scale-95"
                style={ESTILO_WHATSAPP}
              >
                <IconoWhatsApp className="size-3.5" />
                Probar en WhatsApp
              </a>
            )}
          </Campo>
          <Campo etiqueta="Correo" error={errores.email}>
            <Entrada
              type="email"
              inputMode="email"
              value={forma.email ?? ''}
              onChange={(e) => set('email', e.target.value)}
              placeholder="contacto@empresa.cl"
            />
          </Campo>
        </div>

        <div className="grid gap-4 sm:grid-cols-[1.5fr_1fr]">
          <CampoDireccion
            valor={forma.direccion ?? ''}
            onCambiar={(texto) => {
              set('direccion', texto)
              setDireccionConfirmada(false)
            }}
            onElegir={(s) => {
              setForma((f) => ({
                ...f,
                direccion: s.calle,
                comuna: f.comuna?.trim() || s.comuna || f.comuna,
                lat: s.lat,
                lng: s.lng,
              }))
              setDireccionConfirmada(true)
            }}
            confirmada={direccionConfirmada}
            requerido
            error={errores.direccion}
          />
          <Campo
            etiqueta="Comuna"
            error={errores.comuna}
            ayuda={errores.comuna ? undefined : 'Sirve para armar rutas de reparto.'}
          >
            <Entrada
              value={forma.comuna ?? ''}
              onChange={(e) => set('comuna', e.target.value)}
              placeholder="Providencia"
            />
          </Campo>
        </div>

        <Campo etiqueta="Número de local" ayuda="Depto, oficina, local o casa —opcional.">
          <Entrada
            value={forma.local ?? ''}
            onChange={(e) => set('local', e.target.value)}
            placeholder="Local 5"
          />
        </Campo>

        <Campo etiqueta="Observaciones" error={errores.observaciones}>
          <AreaTexto
            value={forma.observaciones ?? ''}
            onChange={(e) => set('observaciones', e.target.value)}
            placeholder="Entregar antes de las 09:00. Recibe por bodega."
          />
        </Campo>
      </form>
    </Hoja>
  )
}
